import React from 'react';
import { motion } from 'framer-motion';
import { useSiteI18n } from '../SiteI18n';
import { SiteLayout } from '../SiteLayout';
import { Badge } from '@/components/ui/badge';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface HeroProps {
  label: string;
  title: string;
  subtitle?: string;
  cta?: boolean;
}

export const SitePageHero: React.FC<HeroProps> = ({ label, title, subtitle, cta = true }) => {
  const { t } = useSiteI18n();
  return (
    <section className="relative pt-32 pb-16 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-gradient-to-br from-primary/25 via-primary/5 to-transparent rounded-full blur-[110px]" />
      </div>
      <div className="container mx-auto px-4 lg:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center"
        >
          <Badge variant="outline" className="mb-5 border-primary/40 text-primary bg-primary/5">{label}</Badge>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight bg-gradient-to-br from-foreground via-foreground to-foreground/50 bg-clip-text text-transparent mb-5 leading-tight">
            {title}
          </h1>
          {subtitle && <p className="text-base md:text-lg text-muted-foreground leading-relaxed">{subtitle}</p>}
          {cta && (
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" className="shadow-xl shadow-primary/30">
                <Link to="/site/contact" className="gap-2">{t.nav.demo} <ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

interface CardListProps {
  items: readonly { t: string; d: string }[];
}

export const SiteCardGrid: React.FC<CardListProps> = ({ items }) => (
  <section className="py-16">
    <div className="container mx-auto px-4 lg:px-6">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((it, i) => (
          <motion.div
            key={it.t}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.04 }}
            className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-md p-6 hover:border-primary/40 hover:-translate-y-1 transition-all"
          >
            <h3 className="font-bold text-lg mb-2">{it.t}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{it.d}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const SiteCtaBlock: React.FC = () => {
  const { t } = useSiteI18n();
  return (
    <section className="py-16">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-10 md:p-14 text-center">
          <h2 className="text-2xl md:text-4xl font-black tracking-tight mb-3">{t.finalCta.title}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">{t.finalCta.subtitle}</p>
          <Button asChild size="lg" className="shadow-xl shadow-primary/30">
            <Link to="/site/contact" className="gap-2">{t.finalCta.btn} <ArrowRight className="h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export const SimpleSitePage: React.FC<{
  label: string;
  title: string;
  subtitle: string;
  items: readonly { t: string; d: string }[];
}> = ({ label, title, subtitle, items }) => (
  <SiteLayout>
    <SitePageHero label={label} title={title} subtitle={subtitle} />
    <SiteCardGrid items={items} />
    <SiteCtaBlock />
  </SiteLayout>
);
